import { Box, Button, Paper, Stack } from '@mui/material';
import GlobalTableGenerator from '../../../../components/GlobalTableGenerator';
import DashboardChart from '../../../refund/DashboardChart';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import FilterSection from '../TopCardOverview/FilterSection';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { typeOfStudent } from '../../../../utils/constants/GlobalStaticData';
import dayjs from 'dayjs';
import { getRefundOverViewTable } from '../../../../redux/features/refundSlice';
import { downloadCSV, makeCSV } from '../../../../components/CustomTableWithPagination/csvExport';

const columns = [
  { field: 'reason', headerName: 'Refund Reason' },
  { field: 'request_count', headerName: 'Count' },
  { field: 'reason_amount', headerName: 'Total Amount' },
];

export default function ReasonOverview() {
  const ref = useRef(null);
  const [height, setHeight] = useState('100%');

  const [showFilters, setShowFilters] = useState(true);
  const [selectedType, setSelectedType] = useState(typeOfStudent[0]);
  const [fromDate, setFromDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD')); //DEFAULT SET TO CURRENT MONTH STARTING DATE TO ENDING DATE
  const [toDate, setToDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [selectedFinanceStatus, setSelectedFinanceStatus] = useState({ id: -1, value: 'All' });

  const dispatch = useDispatch();

  const { data, refundOverviewLoading } = useSelector((state) => state.refund.refundOverviewTable);

  const dispatchParams = useMemo(() => {
    return {
      fromDate,
      toDate,
      type: selectedType?.id,
      financeStatus: selectedFinanceStatus?.id === -1 ? '' : selectedFinanceStatus?.id,
    };
  }, [fromDate, toDate, selectedType, selectedFinanceStatus]);

  const permanentLastRow = useMemo(() => {
    const safeData = Array.isArray(data?.datas) ? data.datas : [];

    return safeData.reduce(
      (acc, values) => ({
        request_count: acc.request_count + (Number(values?.request_count) || 0),
        reason_amount: acc.reason_amount + (Number(values?.reason_amount) || 0),
      }),
      {
        request_count: 0,
        reason_amount: 0,
      },
    );
  }, [data]);

  const filterSubmit = React.useCallback(() => {
    dispatch(getRefundOverViewTable(dispatchParams));
  }, [dispatch, dispatchParams]);

  // Apply the Finance Status filter as soon as it changes (skip the initial mount; the load effect below fetches it)
  const financeStatusMounted = useRef(false);
  useEffect(() => {
    if (!financeStatusMounted.current) {
      financeStatusMounted.current = true;
      return;
    }
    dispatch(getRefundOverViewTable(dispatchParams));
  }, [selectedFinanceStatus]);

  const handleExport = () => {
    const exportRows = [
      ...(data?.datas || []),
      {
        ...permanentLastRow,
        reason: 'TOTAL',
      },
    ];

    const csv = makeCSV({
      headers: columns,
      rows: exportRows,
    });

    downloadCSV(csv, `Refund_Reason_Overview_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`);
  };

  useEffect(() => {
    dispatch(getRefundOverViewTable(dispatchParams));
  }, []);

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current?.clientHeight);
    }
  }, [refundOverviewLoading]);

  return (
    <Stack gap={1}>
      <FilterSection
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        // selectedType={selectedType}
        // setSelectedType={setSelectedType}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        selectedFinanceStatus={selectedFinanceStatus}
        setSelectedFinanceStatus={setSelectedFinanceStatus}
        filterSubmit={filterSubmit}
        loading={false}
      />

      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" onClick={handleExport} disabled={refundOverviewLoading || !data?.datas?.length}>
          Export
        </Button>
      </Stack>
      <Paper elevation={3} p={1}>
        <Box display={'flex'} gap={1} flexDirection={{ xs: 'column', md: 'row' }}>
          <Box ref={ref} flex={1}>
            <DashboardChart
              loading={refundOverviewLoading}
              data={data?.datas ?? []}
              keys={{ label: 'reason', value: 'percentage' }}
              totalCount={data?.total_request || 0}
            />
          </Box>
          <Box width={'100%'} flex={1}>
            <GlobalTableGenerator
              TableContainerStyle={{
                minHeight: { xs: '100%', sm: height },
                border: '1px solid #c9c6c6ff',
                borderRadius: 1,
                borderCollapse: 'collapse',
                '& .MuiTableCell-root': {
                  border: '1px solid #d9d9d9',
                },
              }}
              columns={columns}
              TableStyle={{
                '& tbody tr:last-child .MuiTableCell-root': {
                  fontWeight: '800 !important',
                },
                ...(refundOverviewLoading
                  ? []
                  : {
                      '& tbody tr:last-child': {
                        bgcolor: '#f7d979ff',
                        fontWeight: '800 !important',
                      },
                    }),
              }}
              rows={data?.datas ?? []}
              TableHeadStyle={{
                bgcolor: '#3a79bb',
                '& .MuiTableCell-root': {
                  color: '#fff',
                  fontWeight: 600,
                },
              }}
              permenantLastRow={{ ...permanentLastRow, reason: 'TOTAL' }}
              loading={refundOverviewLoading}
            />
          </Box>
        </Box>
      </Paper>
    </Stack>
  );
}
